import { useEffect, useRef, useState, type FormEvent } from 'react'
import type { BudgetMonth, BudgetCategory } from './types'
import { useMoveMoney } from './hooks'
import { formatCurrency } from './format'

interface Props {
  month: BudgetMonth
  year: number
  monthNumber: number
  open: boolean
  onClose: () => void
  /** Pre-fill source category id (when invoked from a row's menu). */
  initialFromId?: string | null
}

interface FlatCategory {
  id: string
  label: string // "Group / Category" — disambiguates duplicate names
  category: BudgetCategory
}

function flatten(month: BudgetMonth): FlatCategory[] {
  return month.categoryGroups.flatMap((g) =>
    g.categories
      .filter((c) => !c.isHidden)
      .map((c) => ({
        id: c.id,
        label: `${g.name} / ${c.name}`,
        category: c,
      })),
  )
}

/**
 * Native <dialog> would be nicest for keyboard semantics (esc closes,
 * trap focus), but cross-browser quirks (esp. iPadOS) mean we
 * hand-roll the overlay + role="dialog". Focus trap is minimal — the
 * form's tab order is short enough that it's fine.
 */
export function MoveMoneyDialog({ month, year, monthNumber, open, onClose, initialFromId }: Props) {
  const move = useMoveMoney()
  const flat = flatten(month)
  const [fromId, setFromId] = useState<string | ''>(initialFromId ?? '')
  const [toId, setToId] = useState<string | ''>('')
  const [amount, setAmount] = useState('')
  const [error, setError] = useState<string | null>(null)
  const firstFieldRef = useRef<HTMLSelectElement>(null)

  useEffect(() => {
    if (open) {
      setFromId(initialFromId ?? '')
      setToId('')
      setAmount('')
      setError(null)
      firstFieldRef.current?.focus()
    }
  }, [open, initialFromId])

  if (!open) return null

  const fromCat = flat.find((f) => f.id === fromId)?.category

  function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const parsed = Number(amount.replace(/[$,]/g, ''))
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Enter a positive amount')
      return
    }
    if (fromId && fromCat && parsed > fromCat.assigned) {
      setError(`Only ${formatCurrency(fromCat.assigned)} assigned to source`)
      return
    }
    if (fromId === toId && fromId !== '') {
      setError('From and To must differ')
      return
    }
    move.mutate(
      {
        year,
        month: monthNumber,
        fromId: fromId === '' ? null : fromId,
        toId: toId === '' ? null : toId,
        amount: parsed,
      },
      {
        onSuccess: onClose,
        onError: (err) => setError(err instanceof Error ? err.message : 'Move failed'),
      },
    )
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="move-money-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose()
      }}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md space-y-4 rounded-2xl border border-zinc-200 bg-white p-6 shadow-xl dark:border-zinc-800 dark:bg-zinc-900"
      >
        <h2 id="move-money-title" className="text-lg font-semibold">
          Move money
        </h2>

        <label className="block text-sm">
          <span className="mb-1 block text-zinc-600 dark:text-zinc-300">From</span>
          <select
            ref={firstFieldRef}
            value={fromId}
            onChange={(e) => setFromId(e.target.value)}
            className="w-full rounded-md border border-zinc-300 bg-white px-2 py-2 dark:border-zinc-700 dark:bg-zinc-950"
          >
            <option value="">To Be Assigned</option>
            {flat.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label} — {formatCurrency(f.category.assigned)}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="mb-1 block text-zinc-600 dark:text-zinc-300">To</span>
          <select
            value={toId}
            onChange={(e) => setToId(e.target.value)}
            className="w-full rounded-md border border-zinc-300 bg-white px-2 py-2 dark:border-zinc-700 dark:bg-zinc-950"
          >
            <option value="">To Be Assigned</option>
            {flat.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="mb-1 block text-zinc-600 dark:text-zinc-300">Amount</span>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            className="w-full rounded-md border border-zinc-300 bg-white px-2 py-2 tabular-nums dark:border-zinc-700 dark:bg-zinc-950"
            required
          />
        </label>

        {error && (
          <p role="alert" className="text-sm text-overspent">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-zinc-300 px-4 py-2 text-sm hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={move.isPending}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {move.isPending ? 'Moving…' : 'Move'}
          </button>
        </div>
      </form>
    </div>
  )
}
